import { useState, useEffect } from "react";
import { useLogin, useRegister } from "../../../lib/api/users.api";
import { track } from "../../../lib/analytics";
import { getAuthErrorMessage } from "../lib/errorMessages";
import { PasswordField } from "./PasswordField";
import { WaitlistSection } from "./WaitlistSection";
import { AuthHeader } from "./AuthHeader";
import { AuthFooter } from "./AuthFooter";
import { OAuthButtons } from "./OAuthButtons";

const MIN_WORDS = 4;
const MIN_WORD_LENGTH = 3;
const MIN_PASSWORD = 12;

type PasswordMode = "passphrase" | "classic";

function isPassphraseValid(words: readonly string[]): boolean {
  const validWords = words.filter((w) => w.length >= MIN_WORD_LENGTH);
  return validWords.length >= MIN_WORDS;
}

export function LoginFormComponent({
  mode,
  onModeChange,
  onSuccess,
  onRecover,
}: {
  readonly mode: "login" | "register";
  readonly onModeChange: () => void;
  readonly onSuccess: (recoveryKey?: string) => void;
  readonly onRecover: (email: string) => void;
}) {
  const [oauthError] = useState(() => {
    const url = new URL(window.location.href);
    const oauthParam = url.searchParams.get("oauth");
    if (oauthParam) {
      window.history.replaceState({}, "", url.pathname);
    }
    return oauthParam;
  });

  const [wasSuspended] = useState(() => {
    const flag = localStorage.getItem("palnia_suspended");
    if (flag) localStorage.removeItem("palnia_suspended");
    return !!flag;
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passphraseWords, setPassphraseWords] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMode, setPasswordMode] = useState<PasswordMode>("passphrase");
  const [hasAttemptedRegister, setHasAttemptedRegister] = useState(false);

  const login = useLogin();
  const register = useRegister();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  const mutation = mode === "login" ? login : register;
  const hasPassphrase = passwordMode === "passphrase" && isPassphraseValid(passphraseWords);
  const hasClassicPassword = passwordMode === "classic" && password.length >= (mode === "register" ? MIN_PASSWORD : 1);
  const hasPassword = hasPassphrase || hasClassicPassword;

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const isValid =
    isValidEmail &&
    hasPassword &&
    (mode === "login" || name.trim());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    const finalPassword =
      passwordMode === "passphrase"
        ? passphraseWords.join(" ")
        : password;

    const onSuccessHandler = (response: { recoveryKey?: string }) => {
      onSuccess(response.recoveryKey);
    };

    if (mode === "login") {
      login.mutate(
        { email: email.trim(), password: finalPassword },
        { onSuccess: (res) => { track("user_logged_in", {}); onSuccessHandler(res); } },
      );
    } else {
      setHasAttemptedRegister(true);
      register.mutate(
        { email: email.trim(), password: finalPassword, name: name.trim() },
        { onSuccess: (res) => { track("user_signed_up", {}); onSuccessHandler(res); } },
      );
    }
  };

  const handleModeSwitch = () => {
    setPassword("");
    setPassphraseWords([]);
    setPasswordMode("passphrase");
    setHasAttemptedRegister(false);
    login.reset();
    register.reset();
    onModeChange();
  };

  return (
    <div className="min-h-screen min-h-dvh flex items-center justify-center bg-surface px-4 py-8 sm:px-6 sm:py-12 pt-20 sm:pt-24">
      <div className="w-full max-w-sm flex flex-col gap-6 sm:gap-8">
        <AuthHeader mode={mode} />

        <OAuthButtons mode={mode} />

        {wasSuspended && (
          <p className="text-xs text-center text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            Votre compte a été suspendu. Si vous pensez qu'il s'agit d'une erreur, contactez-nous à contact@newalfox.fr.
          </p>
        )}

        {oauthError && (
          <p className="text-xs text-center text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded p-2">
            {oauthError === "email_exists"
              ? "Un compte existe déjà avec cet email. Connectez-vous avec votre mot de passe, puis liez votre compte depuis les Réglages."
              : oauthError === "unverified_email"
                ? "L'email associé à ce compte n'est pas vérifié. Vérifiez-le d'abord auprès du fournisseur."
                : "La connexion a échoué. Veuillez réessayer."}
          </p>
        )}

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-ink-muted">ou</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {mode === "register" && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Votre prénom"
              required
              autoComplete="name"
              className="px-4 py-3 bg-surface-alt border border-border text-ink
                placeholder:text-ink-muted focus:outline-none focus:ring-2
                focus:ring-accent rounded"
            />
          )}

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              required
              autoComplete="email"
              autoFocus
              className="w-full px-4 py-3 bg-surface-alt border border-border text-ink
                placeholder:text-ink-muted focus:outline-none focus:ring-2
                focus:ring-accent rounded"
            />
            {email.trim() && !isValidEmail && (
              <p className="mt-1 text-xs text-red-500">Veuillez entrer une adresse email valide.</p>
            )}
          </div>

          <PasswordField
            passwordMode={passwordMode}
            onPasswordModeChange={setPasswordMode}
            password={password}
            onPasswordChange={setPassword}
            passphraseWords={passphraseWords}
            onPassphraseWordsChange={setPassphraseWords}
            showPassword={showPassword}
            onShowPasswordChange={setShowPassword}
            isRegister={mode === "register"}
            onForgot={mode === "login" ? () => onRecover(email) : undefined}
          />

          <button
            type="submit"
            disabled={mutation.isPending || !isValid}
            className="mt-1 px-4 py-3 bg-accent text-white font-semibold
              hover:opacity-80 disabled:opacity-40 focus:outline-none
              focus:ring-2 focus:ring-accent rounded transition-opacity"
          >
            {mutation.isPending
              ? "Chargement..."
              : mode === "login"
                ? "Se connecter"
                : "Créer mon compte"}
          </button>

          {mutation.isError && (
            <p className="text-xs text-center text-red-500">
              {getAuthErrorMessage((mutation.error as { status?: number }).status, (mutation.error as { message?: string }).message)}
            </p>
          )}
        </form>

        {mode === "register" && (
          <WaitlistSection
            email={email}
            hasAttemptedRegister={hasAttemptedRegister}
            registerIsError={register.isError}
          />
        )}

        <AuthFooter mode={mode} onModeSwitch={handleModeSwitch} />
      </div>
    </div>
  );
}
